import FreeSearchAction from "./FreeSearchAction";
import Icon from "./Icon";
import List from "./List";
import ListItem from "./ListItem";
import Page from "./Page";
import React, { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import Search from "./Search";
import {
  OpenContext,
  PageContext,
  RenderLinkContext,
  SearchContext,
  SelectContext,
} from "../lib/context";
import { RenderLink } from "../types";
import { Transition, Dialog } from "@headlessui/react";
import { useCmdkStore } from "../store";
import { Button } from "src/common/button";
import { CheckmarkRegularIcon, DeleteStrongIcon } from "src/monet/icons";
import RepeatRegularIcon from "src/monet/icons/RepeatRegularIcon";
import LengthenRegularIcon from "src/monet/icons/LengthenRegularIcon";
import ShortenRegularIcon from "src/monet/icons/ShortenRegularIcon";
import LoadingSpinner from "src/common/components/loading-spinner";

interface TextPaletteProps {
  isOpen: boolean;
  textLoading: boolean;
  onClose: () => void;
  onUseResult: () => void;
  onTryAgain: () => void;
  onDiscard: () => void;
  onLengthen: () => void;
  onShorten: () => void;
}

function TextPalette({
  isOpen,
  textLoading,
  onClose,
  onUseResult,
  onTryAgain,
  onDiscard,
  onLengthen,
  onShorten,
}: TextPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const textOutput = useCmdkStore((state) => state.textOutput);
  
  return (
    <div
    >
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          initialFocus={inputRef}
          as="div"
          className="command-palette"
          onClose={() => {
            onClose();
          }}
        >
          <div className="command-palette-content antialiased">
            <div className="fixed inset-0 overflow-y-auto flex items-center justify-center z-neue-cmdk">
              <div className="flex w-[600px] items-start justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-[600px] max-h-full bg-bedrock-black shadow-lg rounded-lg max-w-xl flex flex-col overflow-hidden">
                    <div
                      className="flex-1 overflow-y-auto focus:outline-none p-4 space-y-4"
                      tabIndex={-1}
                    >
                      <div className="text-bedrock-p text-white">
                        {textLoading ? <LoadingSpinner /> : textOutput}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-1">
                          <TextPaletteButton
                            icon={<CheckmarkRegularIcon />}
                            value="Use"
                            onClick={onUseResult} />
                          <TextPaletteButton
                            icon={<RepeatRegularIcon />}
                            value="Try again"
                            onClick={onTryAgain} />
                          <TextPaletteButton
                            icon={<DeleteStrongIcon className="w-4 h-4" />}
                            value="Discard"
                            onClick={onDiscard} />
                        </div>
                        <div className="flex items-center space-x-1">
                          <TextPaletteButton
                            icon={<LengthenRegularIcon />}
                            value="Lengthen"
                            onClick={onLengthen} />
                          <TextPaletteButton
                            icon={<ShortenRegularIcon />}
                            value="Shorten"
                            onClick={onShorten} />
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

const TextPaletteButton = ({
  value,
  icon,
  onClick
}: {
  value: string;
  icon: ReactNode;
  onClick: () => void;
}) => {
  return (
    <button type="button" className="flex items-center space-x-1 hover:bg-white/10 text-white rounded-sm py-[7px] px-2"
      onClick={onClick}>
      {icon && icon}
      <span className="text-bedrock-p-small">{value}</span>
    </button>
  );
}

export default TextPalette;